import { deleteField, Timestamp, UpdateData } from 'firebase/firestore'

/**
 * True for plain `{ ... }` object literals (or `Object.create(null)`) — false for arrays, null,
 * and class instances such as Firestore's `Timestamp` or its `FieldValue` sentinels. We only want
 * to recurse into plain objects; everything else should be treated as an opaque value.
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
	if (value === null || typeof value !== 'object' || Array.isArray(value)) {
		return false
	}
	const proto = Object.getPrototypeOf(value)
	return proto === Object.prototype || proto === null
}

/**
 * Recursively removes `undefined` values so we never hand Firestore's `updateDoc`/`setDoc` a value
 * it will reject client-side. Plain objects have their `undefined` keys dropped; arrays keep their
 * length and indices by mapping `undefined` elements to `null` (Firestore arrays can't hold
 * `undefined`). Anything else — primitives, `null`, class instances like `Timestamp` or `FieldValue`
 * sentinels — is returned untouched so we don't clone or mangle it.
 */
function stripUndefinedDeep(value: unknown): unknown {
	if (Array.isArray(value)) {
		return value.map(item => item === undefined ? null : stripUndefinedDeep(item))
	}
	if (isPlainObject(value)) {
		const result: Record<string, unknown> = {}
		for (const key of Object.keys(value)) {
			const propertyValue = value[key]
			if (propertyValue === undefined) {
				continue
			}
			result[key] = stripUndefinedDeep(propertyValue)
		}
		return result
	}
	return value
}

/**
 * Computes a Firestore update patch (a flat object keyed by dotted field paths) that transforms
 * document state `original` into `changed`.
 *
 * Keys that are present in `original` but absent from `changed` — or explicitly set to `undefined`
 * in `changed` — are deleted with `deleteField()`. Nested plain objects are diffed recursively into
 * dotted paths; anything else that differs (arrays, class instances, primitives, or a plain object
 * paired with a non-plain-object) is replaced wholesale at its key, with `undefined` stripped out of
 * any newly embedded value.
 *
 * When `original` is `undefined` or `{}` the result is a plain flat object with no `deleteField()`
 * sentinels and no dotted keys, so it's safe to use with `setDoc` as well as `updateDoc`.
 */
export function makeFirestorePatch<T>(original: object | undefined, changed: object, prefix = ''): UpdateData<T> {
	const patch: Record<string, unknown> = {}

	const changedRecord = changed as Record<string, unknown>
	const originalRecord = (original ?? {}) as Record<string, unknown>

	/* The union of both objects' keys; the project's TS target can't iterate a Set */
	const keys = Object.keys(changedRecord)
	for (const key of Object.keys(originalRecord)) {
		if (!(key in changedRecord)) {
			keys.push(key)
		}
	}

	for (const key of keys) {
		const fullKey = prefix ? `${prefix}.${key}` : key
		const newVal = changedRecord[key]
		const oldVal = originalRecord[key]

		if (newVal === undefined) {
			if (oldVal === undefined) {
				continue
			}
			patch[fullKey] = deleteField()
		} else if (isPlainObject(newVal) && isPlainObject(oldVal)) {
			Object.assign(patch, makeFirestorePatch(oldVal, newVal, fullKey))
		} else if (newVal instanceof Timestamp && oldVal instanceof Timestamp && newVal.isEqual(oldVal)) {
			continue
		} else if (newVal !== oldVal) {
			patch[fullKey] = stripUndefinedDeep(newVal)
		}
	}

	return patch as UpdateData<T>
}
